import React, { useEffect, useState } from 'react';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { useCountdown } from '../../../hooks/useCountdown.js';
import { LEAVING_COUNTDOWN_MS, leavingText } from '../../../_helpers/shopLeaving.js';
import { SALE_END_RETRIES, SALE_END_RETRY_MS, useSaleClock } from './SkinPriceTag.jsx';
import '../../../styles/SkinTimeLimit.css';

/**
 * Whether an item's sale ends at the same moment as its sale window
 * (`availableUntil`, ENG-834). The backend clamps `saleEndsAt` to
 * `availableUntil`, so this is common; then only the sale's end line shows.
 */
export const sharesSaleEnd = (sale, availableUntil) => {
  if (!sale?.endsAt || !(availableUntil instanceof Date)) return false;
  return Date.parse(sale.endsAt) === availableUntil.getTime();
};

/**
 * The clocks of an item on screen (ENG-832 sale, ENG-834 window): both
 * useCountdown, so its Get it turns off the moment either ends.
 *
 *   { saleClock, windowClock, shared, ended }
 *
 * `sale` is the item's normalized sale (null for none), `availableUntil` its
 * window's end (a Date, null for none). `onEnd()` re-reads the item (the
 * shop's, the shelf's or the try-on offer's); it is asked again a few times
 * while the item still shows the old end, as for a sale. When both end at
 * once (`shared`) it is called once, from the window's clock, and the sale's
 * clock ticks like the window's (by the second in the last 24 hours,
 * ENG-876), since its line then says when the item leaves.
 */
export const useOfferClocks = (sale, availableUntil, onEnd) => {
  const shared = sharesSaleEnd(sale, availableUntil);
  const saleClock = useSaleClock(sale, shared ? null : onEnd, shared ? { secondsWithinMs: LEAVING_COUNTDOWN_MS } : undefined);
  const windowClock = useCountdown(availableUntil ?? null, {
    onEnd,
    repeatMs: SALE_END_RETRY_MS,
    maxRepeats: SALE_END_RETRIES,
    secondsWithinMs: LEAVING_COUNTDOWN_MS,
  });
  return { saleClock, windowClock, shared, ended: saleClock.ended || windowClock.ended };
};

/**
 * Whether the item `id`'s window ran out while it was on screen: true from
 * the moment `ended` turns true, and it stays true for that item after the
 * re-read (which no longer carries the window), so a try-on that stays open
 * can still say "No longer available". Another item starts afresh.
 */
export const useWindowExpired = (id, ended) => {
  const [expiredId, setExpiredId] = useState(null);
  useEffect(() => {
    if (ended && id) setExpiredId(id);
  }, [ended, id]);
  return Boolean(id) && (ended || expiredId === id);
};

/**
 * When a time-limited item leaves the shop (ENG-834, ENG-876), on every skin
 * surface: "Leaving Apr 1, 2027" with more than 24 hours left, in the
 * viewer's time zone; then a live "Leaving in 05:12:09" (useOfferClocks'
 * window clock: by the second, by the minute when the device asks for
 * reduced motion); and "No longer available" once it has left, until the
 * re-read drops it. Nothing without `until`.
 *
 * The ticking clock is hidden from screen readers, which get "Leaving in 5
 * hours" instead; nothing here is a live region, so nothing is announced as
 * it changes. Sits in the item's own text, in normal page flow: never over a
 * broadcast or the performer's video.
 */
const SkinTimeLimit = ({ until, clock = null, className = '', testId = 'skin-time-limit' }) => {
  const { t, format } = useLocale();
  if (!(until instanceof Date) || Number.isNaN(until.getTime())) return null;
  const ended = Boolean(clock?.ended);
  const line = leavingText(t, format, until, { remainingMs: clock?.remainingMs, ended, seconds: clock?.seconds ?? false });
  return (
    <span
      className={`skin-time-limit${ended ? ' skin-time-limit--ended' : ''} ${className}`.trim()}
      data-testid={testId}
      data-ended={ended ? 'true' : undefined}
      data-countdown={line.countdown ? 'true' : undefined}
    >
      {line.countdown ? (
        <>
          <span className="skin-time-limit-sr" data-testid={`${testId}-sr`}>{line.label}</span>
          <span aria-hidden="true" data-testid={`${testId}-clock`}>{line.text}</span>
        </>
      ) : line.text}
    </span>
  );
};

export default SkinTimeLimit;
